import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useToggle } from 'react-use';

import { IState } from 'models/reducers/i-state.interface';
import { IWalletReducer } from 'models/reducers/i-wallet-reducer.interface';
import { IWheelOfFortuneResponse, IWheelOfFortuneReward, WheelKind } from 'models/wheel-of-fortune/i-wheel-of-fortune';

import { ERROR_MESSAGES } from 'constants/error-messages.constant';

import { addErrorMessageAction, setShowCurrencyLotteryModal, setShowSwitchNetworkModal } from 'actions/global-actions';
import { setWalletAssets } from 'actions/wallet.action';

import {
  getWheelOfFortuneResultsService,
  makeWheelOfFortuneBatchSpinService,
  makeWheelOfFortuneSpinService,
} from 'services/wheel-of-fortune.service';

import ElementLoader from 'components/element-loader/ElementLoader';
import ModalPortal from 'components/modal/ModalPortal';

import { queryKeys } from 'hooks/queries/query-keys';
import { useWheelOfFortuneQuery, wheelOfFortuneDataKey } from 'hooks/queries/useWheelOfFortuneQuery';

import { prizeAmount, rewardKindOf, rewardToWheelData } from '../prizeToWheelData';
import { RewardKind, WheelData } from '../WheelOfFortune/types';
import WheelOfFortune from '../WheelOfFortune/WheelOfFortune';
import WheelOfFortuneCountdown from '../WheelOfFortuneCountdown/WheelOfFortuneCountdown';
import WheelOfFortuneHistoryModal from '../WheelOfFortuneHistoryTable/WheelOfFortuneHistoryModal';
import WheelOfFortuneOverlay from '../WheelOfFortuneOverlay/WheelOfFortuneOverlay';
import WheelOfFortuneResult, { IWheelOfFortuneResultRow } from '../WheelOfFortuneResult/WheelOfFortuneResult';
import WheelOfFortuneSafe from '../WheelOfFortuneSafe/WheelOfFortuneSafe';
import WheelOfFortuneSwitch from '../WheelOfFortuneSwitch/WheelOfFortuneSwitch';
import { paletteFor } from '../wheelTheme';
import './WheelOfFortuneModal.scss';

/**
 * What the wheel paints before a payload arrives. Module-level so its identity is stable —
 * WheelOfFortune reloads every segment icon whenever the `data` array it is handed changes.
 */
const BLANK_SEGMENTS: WheelData[] = Array.from({ length: 12 }, () => ({ option: '' }));

/**
 * The most spins one batch request may ask for. The server clamps to the same ceiling AND to
 * the balance, so this number is a label rather than the enforcement - but the button still
 * has to promise what will actually happen. A button offering ten to a holder with seven
 * spins gets clamped to seven server-side and files a support ticket about the missing three,
 * which is why the label is computed from the balance and not from this constant alone.
 */
const MAX_SPINS_PER_BATCH = 10;

const WheelOfFortuneModal = () => {
  const { t } = useTranslation('notifications');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { correctChain } = useSelector(({ web3 }: IState) => web3);
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const { filters } = useSelector((state: IState): IWalletReducer => state.wallet);
  const { assets } = useSelector(({ wallet }: IState): IWalletReducer => wallet);

  const fetchWalletAssets = (page: number, controller: AbortController) => {
    dispatch(setWalletAssets({ ...filters, page: page.toString() }, controller.signal));
  };

  useEffect(() => {
    correctChain && walletAddress && fetchWalletAssets(1, new AbortController());
  }, [correctChain, walletAddress]);

  const [wheelKind, setWheelKind] = useState<WheelKind>('regular');
  const [spinDone, toggleSpinDone] = useToggle(false);
  const [prizeNumber, setPrizeNumber] = useState<number | undefined>(undefined);
  const [lastReward, setLastReward] = useState<string | undefined>(undefined);
  const [lastRewardKind, setLastRewardKind] = useState<RewardKind | undefined>(undefined);
  const [safe, setSafe] = useState<number | undefined>(undefined);
  // Every prize of the batch just spun, in spin order, and what it cost. Undefined for a 1x
  // spin, which keeps rendering from the wheel segment it landed on exactly as before.
  const [batchRows, setBatchRows] = useState<IWheelOfFortuneResultRow[] | undefined>(undefined);
  const [batchSpinsUsed, setBatchSpinsUsed] = useState<number | undefined>(undefined);
  const [showHistory, setShowHistory] = useState(false);

  const isVip = wheelKind === 'vip';
  const palette = paletteFor(wheelKind);

  const { historyData, data: wheelOfFortuneData } = useWheelOfFortuneQuery({ kind: wheelKind });
  // Subscribe to the other wheel as well, so the switch can show both counts at once and
  // flipping between them is served from cache instead of re-issuing a request per switch.
  // The wheel you are NOT looking at, for the count on the switch. Silent on failure:
  // a missing balance there is not worth a toast at someone who never opens that tab.
  const { data: otherWheelData } = useWheelOfFortuneQuery({
    kind: isVip ? 'regular' : 'vip',
    reportErrors: false,
  });

  const wheelDataKey = wheelOfFortuneDataKey(wheelKind, walletAddress);

  // The segments are the payload's rewards, and fall back to blanks so the loader gate
  // (`data[0].option`) always has something to read.
  //
  // DELIBERATE: signed out, the wheel keeps its segments rather than blanking. The endpoint
  // is AllowAny and answers an anonymous caller with the full reward list and
  // `spin_balance: 0`, so a logged-out visitor sees the prizes with the connect-wallet CTA
  // over them. Do NOT "fix" this back to blanking: blank segments make `data[0].option`
  // empty, which flips the loader gate on, and that state is terminal while signed out —
  // it hid the wheel entirely (`visibility: hidden`) behind a spinner that could never
  // resolve. Gate the query with `enabled: !!walletAddress` if the prize list ever has to
  // be withheld until connect; that is a product decision, not a bug fix.
  //
  // Memoised on `rewards` rather than the whole payload: WheelOfFortune rebuilds its canvas
  // and re-loads every segment icon when this array's identity changes, and the optimistic
  // balance write during a spin replaces the payload object while keeping `rewards` intact.
  const rewards = wheelOfFortuneData?.rewards;
  const data = useMemo<WheelData[]>(
    () => (rewards?.length ? rewards.map(rewardToWheelData) : BLANK_SEGMENTS),
    [rewards]
  );
  const spinNumber = wheelOfFortuneData?.next_spin_number ?? null;

  const handleReset = () => {
    toggleSpinDone(false);
    setPrizeNumber(undefined);
    setLastReward(undefined);
    setLastRewardKind(undefined);
    setSafe(undefined);
    setBatchRows(undefined);
    setBatchSpinsUsed(undefined);
  };

  useEffect(() => {
    !walletAddress && handleReset();
  }, [walletAddress]);

  useEffect(() => {
    if (!correctChain) {
      dispatch(setShowSwitchNetworkModal(true));
      handleReset();
    } else {
      dispatch(setShowSwitchNetworkModal(false));
    }
  }, [correctChain]);

  const handleClose = () => {
    dispatch(setShowCurrencyLotteryModal(false));
    handleReset();
  };

  // Get lastReward from history (first page, first item is the most recent)
  useEffect(() => {
    if (historyData?.results && historyData.results.length > 0) {
      const lastRewardItem = historyData.results.find((item) => item.type === 'in' && item.reward);
      if (lastRewardItem?.reward) {
        setLastReward(prizeAmount(lastRewardItem.reward.prize));
        setLastRewardKind(rewardKindOf(lastRewardItem.reward.prize));
      }
    }
  }, [historyData]);

  // Fetch safe value from results endpoint (all results, not paginated). Only RMV counts
  // towards the safe, so non-token prizes contribute nothing.
  const handleFetchSafe = async () => {
    try {
      const response = await getWheelOfFortuneResultsService(new AbortController().signal);
      const safeValue = response.data.reduce(
        (sum, { reward }) => sum + (reward.prize?.kind === 'token' ? parseFloat(reward.prize.token_amount ?? '0') : 0),
        0
      );
      setSafe(safeValue);
    } catch (error: any) {
      // Silently fail - safe is not critical
    }
  };

  useEffect(() => {
    if (walletAddress) {
      handleFetchSafe();
    }
  }, [walletAddress]);

  const handleSwitchWheel = (kind: WheelKind) => {
    if (kind === wheelKind) return;
    handleReset();
    setWheelKind(kind);
  };

  /** Moves the cached spin balance by `delta`, so the badge and the spin gates stay honest. */
  const adjustCachedSpinBalance = (delta: number) => {
    queryClient.setQueryData(wheelDataKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        spin_balance: Math.max(0, (oldData.spin_balance || 0) + delta),
      };
    });
  };

  /**
   * What the result view should print for one won reward.
   *
   * Prefers the segment on the wheel in front of the user, so a batch row looks exactly like
   * the 1x result does today. Falls back to the API's own reward when the id is not on the
   * current segment list - an admin can deactivate a segment or edit the wheel mid-session,
   * and today that only means the wheel does not turn; in a batch it must NOT silently drop
   * the row, because that is a prize the holder won and would never see.
   */
  const rowForReward = (reward: IWheelOfFortuneReward): IWheelOfFortuneResultRow => {
    const source = data.find((item) => item.rewardId === reward.id) ?? rewardToWheelData(reward);
    return {
      reward: source.option,
      rewardKind: source.rewardKind,
      prizeName: source.prizeName,
      iconUrl: source.iconUrl,
    };
  };

  /** Only RMV counts towards the safe; every other prize is credited to a balance. */
  const safeValueOf = (reward: IWheelOfFortuneReward) =>
    reward.prize?.kind === 'token' ? parseFloat(reward.prize.token_amount ?? '0') : 0;

  const handleMakeWheelOfFortuneSpin = async (spinNumber: number, count: number = 1) => {
    try {
      // Optimistically spend what this request is about to spend. It has to be `count` and
      // not 1: the badge would otherwise read nine spins for the eleven seconds until the
      // wheel lands, and `availableSpins` would keep the batch button and "Spin again"
      // enabled for a holder with nothing left - which buys them a 429 toast, not a spin.
      adjustCachedSpinBalance(-count);

      if (count > 1) {
        const batchResponse = await makeWheelOfFortuneBatchSpinService(
          spinNumber,
          new AbortController().signal,
          wheelKind,
          count
        );

        // Render what COMMITTED, never what was requested. Each spin is its own transaction
        // server-side, so a batch can legitimately come up short; `results` is then the
        // honest list and the difference is still sitting in the balance. Handing those
        // unspent spins back keeps the badge right until `onStopSpinning` refetches for real.
        const results = batchResponse.data.results ?? [];
        if (!results.length) throw new Error('Batch spin returned no results');
        adjustCachedSpinBalance(count - results.length);

        setBatchRows(results.map((item) => rowForReward(item.reward)));
        setBatchSpinsUsed(results.length);

        // ONE animation, exactly as today: the wheel is handed a single index and nothing in
        // WheelOfFortune changes. It is the LAST result's index, so the pointer comes to rest
        // on the prize the list ends with. A result whose segment is missing is skipped
        // rather than landed on; if none of them are on the wheel we still have to hand over
        // an index, or `prizeNumber` stays undefined, the wheel never turns, `spinDone` never
        // flips and the prizes the holder just paid for are never shown.
        let landingIndex = -1;
        for (let index = results.length - 1; index >= 0 && landingIndex < 0; index -= 1) {
          landingIndex = data.findIndex((item) => item.rewardId === results[index].reward.id);
        }
        setPrizeNumber(landingIndex < 0 ? 0 : landingIndex);

        setSafe((prevSafe) => results.reduce((sum, item) => sum + safeValueOf(item.reward), prevSafe || 0));

        return;
      }

      const response = await makeWheelOfFortuneSpinService(spinNumber, new AbortController().signal, wheelKind);

      const prize = data?.find((item) => item.rewardId === response.data.reward.id);

      if (prize) {
        setPrizeNumber(data?.indexOf(prize));
      }

      // Only RMV prizes go to the safe.
      if (response.data.reward.prize?.kind === 'token') {
        const rewardValue = parseFloat(response.data.reward.prize.token_amount ?? '0');
        setSafe((prevSafe) => (prevSafe || 0) + rewardValue);
      }

      // Don't refresh here - wait until spinning stops. A refetch landing now would put the
      // new history entry on screen as `lastReward` while `spinDone` is still false, and
      // `lastSpin` would render the prize before the wheel has reached it.
      // Safe will be refetched after spinning stops
    } catch (error) {
      // Revert optimistic update on error
      queryClient.invalidateQueries({ queryKey: wheelDataKey });
      dispatch(addErrorMessageAction(t(ERROR_MESSAGES.default)));
    }
  };

  const handleSpin = () => {
    if (spinNumber) {
      handleMakeWheelOfFortuneSpin(spinNumber);
    }
  };

  const handleSpinAgain = () => {
    toggleSpinDone(false);
    setPrizeNumber(undefined);
    setBatchRows(undefined);
    setBatchSpinsUsed(undefined);

    // Automatically start spinning after 200ms. Read the spin number out of the cache when
    // the timer fires rather than closing over it: the wheel stopping invalidated the
    // payload a moment ago, so the value captured at click time is the one just spent.
    setTimeout(() => {
      const currentSpinNumber = queryClient.getQueryData<IWheelOfFortuneResponse>(wheelDataKey)?.next_spin_number;
      if (currentSpinNumber) {
        handleMakeWheelOfFortuneSpin(currentSpinNumber);
      }
    }, 200);
  };

  const handleSpinBatch = () => {
    if (isSpinning) return;

    if (spinDone) {
      // Same dance as "Spin again": clear the landed state first, then read the spin number
      // and the balance back out of the cache when the timer fires. The wheel stopping
      // invalidated the payload a moment ago, so the values captured at click time are the
      // ones just spent - and re-clamping there is what stops a second batch from asking for
      // more spins than the first one left behind.
      toggleSpinDone(false);
      setPrizeNumber(undefined);
      setBatchRows(undefined);
      setBatchSpinsUsed(undefined);

      setTimeout(() => {
        const cached = queryClient.getQueryData<IWheelOfFortuneResponse>(wheelDataKey);
        const nextSpendable = (cached?.spin_balance ?? 0) + (cached?.has_daily_free_spin ? 1 : 0);
        // Re-checked against the FULL ten, not against "more than one". The batch is all or
        // nothing, so a holder left with nine after the last one gets no second batch - the
        // control has already disappeared behind the result overlay by the time this fires.
        if (cached?.next_spin_number && nextSpendable >= MAX_SPINS_PER_BATCH) {
          handleMakeWheelOfFortuneSpin(cached.next_spin_number, MAX_SPINS_PER_BATCH);
        }
      }, 200);
      return;
    }

    if (spinNumber && canSpinBatch) {
      handleMakeWheelOfFortuneSpin(spinNumber, MAX_SPINS_PER_BATCH);
    }
  };

  const readyToSpin = spinNumber ? !isNaN(spinNumber) : false;
  const currentSpin = prizeNumber !== undefined && data[prizeNumber] && spinDone;
  // `lastReward` stays the last term rather than being wrapped in Boolean(): TypeScript
  // narrows through a const alias like this, so `reward={lastReward}` below is known to
  // be a string. Boolean() collapses it to plain boolean and loses that.
  const lastSpin = !spinDone && lastReward;
  const nextSpinTime = wheelOfFortuneData?.seconds_to_next_spin;
  // The VIP wheel has no daily free spin — it is spun only by holding a VIP spin.
  const hasFreeSpinAvailable = Boolean(wheelOfFortuneData?.has_daily_free_spin);
  const availableSpins = (wheelOfFortuneData?.spin_balance || 0) + (hasFreeSpinAvailable ? 1 : 0);
  const hasAvailableSpin = availableSpins > 0 && readyToSpin;
  // The balance alone, for the half of the switch that shows the VIP wheel — which has no
  // free spin to add. Everything about the BATCH counts `availableSpins` instead: the
  // server spends the free spin first and then the balance, so a control that left it out
  // would offer nine to a holder who can spend ten.
  const spinBalance = wheelOfFortuneData?.spin_balance || 0;
  // THE BATCH IS ALL OR NOTHING AT TEN, and the control is offered only to a holder who can
  // pay for all ten. It used to offer whatever was spendable, so it read "Spin x3" at three
  // and "Spin x7" at seven; the label is a constant now precisely because the offer is.
  //
  // That keeps the promise trivially honest -- the server clamps to what is spendable, and
  // ten is spendable or the button is not there -- and it keeps GO meaning one spin and the
  // batch meaning ten, with nothing in between to explain.
  const canSpinBatch = availableSpins >= MAX_SPINS_PER_BATCH;
  const isSaveVisible = (currentSpin || lastSpin) && !Boolean(assets?.length) && safe;

  const otherSpins = otherWheelData?.spin_balance;
  // The OTHER wheel's spendable total, for the half of the switch that is not in view.
  // Only the regular wheel has a free spin, so only that side ever adds one.
  //
  // `undefined` when that payload has not arrived, NEVER 0. The switch hides the count for
  // `undefined` and paints it for 0, so collapsing the two would advertise a hard "0" for a
  // wheel whose balance nobody has read yet — and the VIP half of this same switch reports
  // unknown by passing `otherSpins` straight through. The two halves have to agree about
  // what "we do not know" looks like.
  const otherRegularSpins =
    otherSpins === undefined
      ? undefined
      : otherSpins + (otherWheelData?.has_daily_free_spin ? 1 : 0);
  // Mid-spin the wheel owns `prizeNumber` until it reports back; switching then would
  // swap the segments out from under the animation and land the pointer on a prize from
  // the other wheel.
  const isSpinning = prizeNumber !== undefined && !spinDone;
  // One accent per wheel, used by every action in this modal.
  const accentClass = isVip ? 'wof-btn--amber' : 'wof-btn--primary';

  return (
    <ModalPortal
      title="Wheel of Fortune"
      onClose={handleClose}
      modalContentClassName="wheel-of-fortune-modal-content tw-w-full tw-pt-5 tw-px-5 lg:tw-px-10 tw-pb-10 md:tw-max-w-screen-md md:tw-px-0 tw-bg-transparent"
      titleClassName="!tw-text-[clamp(26px,4.4vh,48px)]"
    >
      <ElementLoader loading={!Boolean(data[0].option.length)} className="tw-bg-transparent">
        {!walletAddress && <WheelOfFortuneOverlay />}
        <div className="wof-stack tw-text-white">
          <p className="-tw-mt-5 tw-text-center tw-text-[18px]">
            {isVip ? 'Spend a VIP Spin for the best rewards!' : 'Collect a free spin every day!'}
          </p>

          <WheelOfFortuneSwitch
            value={wheelKind}
            onChange={handleSwitchWheel}
            // THE SWITCH IS THE COUNT. It replaced a separate "Available Spins" badge,
            // so what it shows has to be everything the holder can actually spend --
            // the balance PLUS today's free spin, on whichever wheel offers one. Only
            // the regular wheel does; the VIP wheel is balance alone.
            regularSpins={isVip ? otherRegularSpins : availableSpins}
            vipSpins={isVip ? spinBalance : otherSpins}
            disabled={isSpinning}
          />

          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[10px]">
            {/* Only the regular wheel refills on its own, so only it has a countdown. */}
            {!isVip && !hasFreeSpinAvailable && nextSpinTime && (
              <WheelOfFortuneCountdown spinTime={nextSpinTime} variant="compact" />
            )}
          </div>

          <div className="tw-relative tw-flex tw-w-full tw-justify-center">
            {currentSpin && (
              <WheelOfFortuneResult
                reward={data[prizeNumber].option}
                rewardKind={data[prizeNumber].rewardKind}
                prizeName={data[prizeNumber].prizeName}
                iconUrl={data[prizeNumber].iconUrl}
                // Only THIS instance ever lists more than one prize. The `lastSpin` copy
                // below is a teaser built from the newest history row, which is a single
                // reward however many the last batch won.
                rows={batchRows}
                spinsUsed={batchSpinsUsed}
                accentClass={accentClass}
                onClose={handleClose}
                hasAvailableSpin={hasAvailableSpin}
                onSpinAgain={handleSpinAgain}
              />
            )}
            <WheelOfFortune
              loading={!Boolean(data[0].option.length)}
              prizeNumber={prizeNumber}
              data={data}
              onSpin={handleSpin}
              variant={isVip ? 'vip' : 'default'}
              onStopSpinning={() => {
                toggleSpinDone(true);
                // Refresh cache only after spinning stops - this will refetch both data and history
                // lastReward will be updated automatically from historyData via useEffect.
                // A wheel can pay out spins — including the other wheel's — so the whole
                // prefix goes, not just this wheel's payload.
                queryClient.invalidateQueries({ queryKey: queryKeys.wheelOfFortune.all });
                // AND THE VIP BALANCE, because a spin moves it whether or not the wheel
                // landed on VIP Points: `lottery/views.py:spin()` grants a flat
                // `vip_points_per_spin` bonus on EVERY spin, on top of whatever the
                // segment paid. Without this the number sitting behind the modal on the
                // VIP route stays at its pre-spin value until something else happens to
                // invalidate it, and the holder reads that as points not arriving.
                //
                // The whole `vipUser` prefix rather than this wallet's key: the balance
                // is one of several fields on that payload and they all move together.
                queryClient.invalidateQueries({ queryKey: queryKeys.vipUser.all });
                // Fetch safe value after refresh (only when needed, not on every spin)
                handleFetchSafe();
              }}
              disabled={!readyToSpin}
            />
            {lastSpin && (
              <WheelOfFortuneResult
                reward={lastReward}
                rewardKind={lastRewardKind}
                accentClass={accentClass}
                onClose={handleClose}
                hasAvailableSpin={hasAvailableSpin}
                onSpinAgain={handleSpinAgain}
              />
            )}
          </div>

          {isSaveVisible && (
            <div className="tw-w-[296px]">
              <WheelOfFortuneSafe value={safe} />
            </div>
          )}

          <div className="tw-flex tw-items-center tw-justify-center tw-gap-[10px]">
            {/*
              Absent below ten spins rather than disabled: a permanently dead control on the
              wheel's busiest row is noise, and anything under ten is what the GO button is
              for. It disappears the moment the balance drops below ten -- including on the
              optimistic decrement fired as a batch starts, which is what stops a second
              batch being fired against spins already committed to the first.
            */}
            {canSpinBatch && (
              <button
                type="button"
                aria-label={`Spin ${MAX_SPINS_PER_BATCH} times`}
                className={`wof-btn wof-btn--sm ${accentClass}`}
                onClick={handleSpinBatch}
                disabled={!readyToSpin || isSpinning}
              >
                Spin x{MAX_SPINS_PER_BATCH}
              </button>
            )}

            <button
              type="button"
              aria-label="History"
              className={`wof-btn wof-btn--sm ${accentClass}`}
              onClick={() => setShowHistory(true)}
            >
              History
            </button>
          </div>
        </div>
      </ElementLoader>
      {showHistory && <WheelOfFortuneHistoryModal onClose={() => setShowHistory(false)} />}
    </ModalPortal>
  );
};

export default WheelOfFortuneModal;
